import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useCallback, useState } from 'react'

interface IProps {
  pageTitle?: string
  submitButtonLabel?: string
  onSubmit?: () => Promise<void> | void
  onBack?: () => void
}

export const VesselVisitPlanningPageHeader: FC<IProps> = observer(
  ({ pageTitle, submitButtonLabel, onSubmit, onBack }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { appViewStore } = usePlanningStore()

    const [isSaving, setIsSaving] = useState(false)

    const handleSaveVesselVisit = useCallback(async () => {
      try {
        appViewStore.showLoader()
        setIsSaving(true)

        if (onSubmit) {
          await onSubmit()
        }
      } catch (error) {
        appViewStore.setShowAlert(
          'error',
          t('vesselVisitCreationFailed', 'Vessel Visit Creation Failed'),
        )
      } finally {
        appViewStore.hideLoader()
        setIsSaving(false)
      }
    }, [appViewStore, onSubmit, t])

    return (
      <Stack
        paddingX={theme.customSpacing.l}
        paddingY={theme.customSpacing.l}
        marginBottom={theme.customSpacing.l}
        alignItems='center'
        direction='row'
        sx={{ placeContent: 'space-between' }}
        borderBottom={`1px solid ${theme.palette.divider}`}
      >
        <Stack alignItems='flex-start'>
          <Typography variant='h3' align='center'>
            {pageTitle ?? t('Create vessel visit')}
          </Typography>
        </Stack>

        <Stack gap={theme.customSpacing.m} direction='row' alignItems='center'>
          <Button
            variant='text'
            onClick={onBack}
            color='inherit'
            data-cy='vessel-visit-header-cancel-btn'
          >
            {t('cancel', 'Cancel')}
          </Button>

          <Button
            disabled={isSaving}
            variant='contained'
            color='primary'
            onClick={handleSaveVesselVisit}
            data-cy='vessel-visit-header-submit-btn'
            type='button'
            startIcon={isSaving && <CircularProgress size='1rem' />}
          >
            {submitButtonLabel ?? t('submit', 'Submit')}
          </Button>
        </Stack>
      </Stack>
    )
  },
)
